import React from 'react';
import classNames from 'classnames/bind';
import { useTranslation } from 'react-i18next';
import MenuTab from 'koba/components/MobileMenu/components/MenuTab';
import UserAvatar from 'components/UserAvatar';
import { useCommunityContext } from 'components/CommunityContext';
import { hasModeratorBadge, isAdmin } from 'utils/userHelpers';
import { useWindowSize } from 'hooks/useWindowSize';
import { MenuItem, Text } from '@thinkific/chalk';
import {
  BookmarkSimple,
  Gear,
  SignOut,
  SquaresFour,
  User,
} from '@thinkific/chalk/react-icons';
import TrackingEvent from 'constants/trackingEvents';
import ModeratorBadge from 'components/ModeratorBadge';

import { getFollowingUrl, getSettingsUrl, routes } from 'utils/routerHelpers';
import { atomic } from '@thinkific/chalk/atomic';
import styles from './UserAvatarMenu.module.scss';

const cx = classNames.bind(styles);
interface UserAvatarMenuProps {
  isOpen: boolean;
  onClose: () => void;
  communityId: string;
  user: {
    fullName: string;
    avatarUrl: string;
    id: string;
    isModerator: boolean;
  };
}

const UserAvatarMenu = ({
  isOpen,
  onClose,
  communityId,
  user,
}: UserAvatarMenuProps) => {
  const { t } = useTranslation();
  const { trackEvent, currentUser } = useCommunityContext();
  const { isMobile } = useWindowSize();
  const showModeratorBadge = hasModeratorBadge(user);
  const RoleBadge = () => {
    const label = showModeratorBadge
      ? t('common-Moderator', 'Moderator')
      : t('common-Member', 'Member');
    return (
      <div className={cx('side-menu__moderator-badge__container')}>
        <ModeratorBadge>{label as string}</ModeratorBadge>
      </div>
    );
  };

  const goTo = (route: string, event?: TrackingEvent) => {
    if (event) trackEvent(event, {});
    window.location.href = route;
  };

  const AdminItems = () => (
    <>
      <MenuItem
        href={getSettingsUrl(communityId)}
        icon={Gear}
        iconPosition="lead"
        label={t(
          'components-currentUserDropdown-settings_button',
          'Community settings'
        )}
        onClick={() => goTo(getSettingsUrl(communityId))}
      />
      <MenuItem
        href={routes.communities}
        icon={SquaresFour}
        iconPosition="lead"
        label={t(
          'components-currentUserDropdown-all_communities',
          'All communities'
        )}
        onClick={() => goTo(routes.communities)}
      />
    </>
  );

  const StudentItems = () => (
    <>
      <MenuItem
        href={routes.dashboard}
        icon={SquaresFour}
        iconPosition="lead"
        label={t(
          'communities_components-currentUserDropdown-my_dashboard_button',
          'My dashboard'
        )}
        onClick={() => goTo(routes.dashboard)}
      />
    </>
  );

  return (
    <MenuTab
      hasOverlay={isMobile}
      isOpen={isOpen}
      menuTitle={t('communities_Profile', 'Profile')}
      size={isMobile ? 'medium' : 'large'}
      slideTo="left"
      onClose={onClose}
    >
      <aside
        aria-label={t(
          'communities_components-userAvatarMenu-aria-label',
          'User Avatar Menu, a list of options pertaining to the user and community'
        )}
        className={cx('side-menu')}
        data-qa="side-menu__wrapper"
        role="dialog"
      >
        <div className={cx('side-menu__content')}>
          <div className={cx('side-menu__header')}>
            <UserAvatar
              avatarUrl={user.avatarUrl}
              className={cx('side-menu__avatar')}
              size="md"
              srcSet={`${user.avatarUrl}?width=148 1x, ${user.avatarUrl}?width=148&dpr=2 2x, ${user.avatarUrl}?width=148&dpr=3 3x`}
              userId={user.id}
              username={user.fullName}
            />
          </div>
          <div className={cx('side-menu__info')}>
            {user.fullName && (
              <Text as="h4" variant="display.sm">
                {user.fullName}
              </Text>
            )}
            <div className={cx('side-menu__label')}>
              <RoleBadge />
            </div>
          </div>
        </div>

        <section className={cx('side-menu__content')}>
          <div className={cx('side-menu__buttons')}>
            <MenuItem
              href={getFollowingUrl(communityId)}
              icon={BookmarkSimple}
              iconPosition="lead"
              label={t('communities_Following": "Following"', 'Following')}
              onClick={() => goTo(getFollowingUrl(communityId))}
            />
            {isAdmin(currentUser) && <AdminItems />}
            {!isAdmin(currentUser) && <StudentItems />}
            <MenuItem
              href={routes.myAccount}
              icon={User}
              iconPosition="lead"
              label={t(
                'components-currentUserDropdown-edit_profile_button',
                'Edit profile'
              )}
              onClick={() => goTo(routes.myAccount, TrackingEvent.MY_ACCOUNT)}
            />
            <MenuItem
              className={atomic({ color: 'action.destructive.dark' })}
              href={routes.signOut}
              icon={SignOut}
              iconPosition="lead"
              label={t(
                'components-currentUserDropdown-log_out_button',
                'Log out'
              )}
              variant="destructive"
              onClick={() => {
                localStorage.removeItem('draftPosts');
                goTo(routes.signOut);
              }}
            />
          </div>
        </section>
      </aside>
    </MenuTab>
  );
};

export default UserAvatarMenu;
